import React, { useState, useEffect } from 'react';
import { theme } from '../../theme';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import ChartDataLabels from 'chartjs-plugin-datalabels';

// Import MUI styling for dropdown filter
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import InputLabel from '@mui/material/InputLabel';
import NativeSelect from '@mui/material/NativeSelect';
import FormControl from '@mui/material/FormControl';
import Skeleton from '@mui/material/Skeleton';

ChartJS.register(
  ChartDataLabels,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
);

function OpsChart({ parentToChild, onResetChart }) {
  const { startDate, endDate, selectedOption, selectedRadio, isBeingResetChart, selectedSubReasonFromChild, selectedReasonFromChild, selectedCallCenter, mode } = parentToChild;

  const [result, setData] = useState([]);
  const [range, setRange] = useState('');
  const [selection, setSelection] = useState('All');
  const [isLoading, setIsLoading] = useState(true);
  const [displayText, setDisplayText] = useState('');

  /* FOR DEBUG USE */
  // useEffect (() => {
  //   console.log('USE EFFECT PROD CHANGE')
  //   console.log('selected product is ' + selectedSubReasonFromChild)
  // }, [selectedSubReasonFromChild])

  // useEffect (() => {
  //   console.log('USE EFFECT CALL REASON CHANGE')
  //   console.log('selected callreason is ' + selectedReasonFromChild)
  // }, [selectedReasonFromChild])

  const handleChange = (event) => {
    setRange(event.target.value);
  };

  const handleSelection = (event) => {
    setSelection(event.target.value);
  };

  // Reset all useState variables when reset button is pressed in parent component
  useEffect(() => {
    if (isBeingResetChart === true) {
      setRange('');
      setSelection('All');
      setDisplayText('');
      onResetChart();
    }
  }, [isBeingResetChart, onResetChart]);

  useEffect(() => {
    if (selectedSubReasonFromChild !== '' && selectedReasonFromChild === '') {
      setSelection('By Call Sub Reason');
    } else if (selectedReasonFromChild !== '' && selectedSubReasonFromChild === '') {
      setSelection('By Call Reason');
    } else if (selectedSubReasonFromChild !== '' && selectedReasonFromChild !== '') {
      setSelection('All');
    }
  }, [selectedSubReasonFromChild, selectedReasonFromChild]);

  useEffect(() => {
    setIsLoading(true); // Set loading state to true before fetching data
    
    const formattedStartDate = startDate.toISOString().split('T')[0];
    const formattedEndDate = endDate.toISOString().split('T')[0];
    let formattedCallCenter;
    if (selectedCallCenter === 'All') {
        formattedCallCenter = '&call_center_id=';
    } else {
        formattedCallCenter = '&call_center_id=' + selectedCallCenter;
    }

    let apiUrl = '';

    switch (selectedRadio) {
      case 'total_call_volume':
        apiUrl = `/volume_graph?start_date=${formattedStartDate}&end_date=${formattedEndDate}`;
        setDisplayText('Total Call Volume');
        break;
      case 'average_wait_time':
        apiUrl = `/average_wait_time_graph?start_date=${formattedStartDate}&end_date=${formattedEndDate}`;
        setDisplayText('Average Wait Time');
        break;
      case 'average_handle_time':
        apiUrl = `/average_handle_time_graph?start_date=${formattedStartDate}&end_date=${formattedEndDate}`;
        setDisplayText('Average Handle Time');
        break;
      case 'resolution_rate':
        apiUrl = `/resolution_rate_graph?start_date=${formattedStartDate}&end_date=${formattedEndDate}`;
        setDisplayText('Resolution Rate');
        break;
      case 'average_sentiment':
        apiUrl = `/average_sentiment_graph?start_date=${formattedStartDate}&end_date=${formattedEndDate}`;
        setDisplayText('Average Sentiment');
        break;
      case 'unresolved_calls':
        apiUrl = `/total_unresolved_graph?start_date=${formattedStartDate}&end_date=${formattedEndDate}`;
        setDisplayText('Unresolved Calls');
        break;
      default:
        apiUrl = `/volume_graph?start_date=${formattedStartDate}&end_date=${formattedEndDate}`;
        setDisplayText('Total Call Volume');
    }

    fetch(`${apiUrl}${formattedCallCenter}`)
      .then((res) => res.json())
      .then((result) => {
        setData(result);
        setIsLoading(false); // Set loading state to false after fetching data
      });
  }, [startDate, endDate, selectedRadio, selectedCallCenter]);

  let dataKey;
  let dataLabel;
  switch (selectedRadio) {
    case 'total_call_volume':
      dataKey = 'call_volume';
      dataLabel = 'Call Volume';
      break;
    case 'average_wait_time':
      dataKey = 'average_waittime';
      dataLabel = 'Average Wait Time (seconds)';
      break;
    case 'average_handle_time':
      dataKey = 'average_handletime';
      dataLabel = 'Average Handle Time (minutes)';
      break;
    case 'resolution_rate':
      dataKey = 'resolution_rate';
      dataLabel = 'Resolution Rate (%)';
      break;
    case 'average_sentiment':
      dataKey = 'average_sentiment';
      dataLabel = 'Average Sentiment (10 - Highest)';
      break;
    case 'unresolved_calls':
      dataKey = 'total_unresolved';
      dataLabel = 'Total Unresolved';
      break;
    default:
      dataKey = 'call_volume';
      dataLabel = 'Call Volume';
  }

  // Process result data for charts
  // Product data
  let aggregatedData = [];
  let transactionLimitsInquiryData = [];
  let unauthorizedTransactionData = [];
  let transactionFailureData = [];
  let technicalAssistanceData = [];
  let updatePersonalInformationData = [];
  let accountClosureOrUpgradeData = [];
  let eligibilityAndRequirementsData = [];
  let productFeaturesInquiryData = [];
  let accountOpeningAssistanceData = [];

  // Call reason data
  let accountMgmtData = [];
  let onlineMobileBankingData = [];
  let transactionIssuesData = [];
  let accountSetupData = [];

  const aggregateDataByDate = (entries, dataKey) => entries.reduce((acc, entry) => {
    let entryDate;
    if (range === 'W') {
      const date = new Date(entry.date);
      date.setDate(date.getDate() - date.getDay());
      entryDate = date.toISOString().split('T')[0];
    } else {
      entryDate = entry.date;
    }

    const existingEntry = acc.find((item) => item.date === entryDate);
    if (existingEntry) {
      existingEntry[dataKey] += entry[dataKey];
    } else {
      acc.push({ date: entryDate, [dataKey]: entry[dataKey] });
    }
    return acc;
  }, []);

  let productAggregatedData = {};
  if (selection === 'All') {
    aggregatedData = aggregateDataByDate(result, dataKey);
    //console.log(aggregatedData)
  } else if (selection === 'By Call Sub Reason') {
    //console.log(result);

    // Aggregate result based on radio button selection
    if (selectedRadio === 'total_call_volume' || selectedRadio === 'unresolved_calls') {
      result.forEach(entry => {
        const productName = entry.callsubreason;

        if (!productAggregatedData[productName]) {
          productAggregatedData[productName] = [];
        }

        let entryDate;
        if (range === 'W') {
          const date = new Date(entry.date);
          date.setDate(date.getDate() - date.getDay());
          entryDate = date.toISOString().split('T')[0];
        } else {
          entryDate = entry.date;
        }

        const existingEntry = productAggregatedData[productName].find(item => item.date === entryDate);
        if (existingEntry) {
          existingEntry[dataKey] += entry[dataKey];
        } else {
          productAggregatedData[productName].push({ date: entryDate, [dataKey]: entry[dataKey] });
        }
      });

      //console.log(productAggregatedData)

      transactionLimitsInquiryData = productAggregatedData['Transaction Limits Inquiry'] || [];
      unauthorizedTransactionData = productAggregatedData['Unauthorized Transaction'] || [];
      transactionFailureData = productAggregatedData['Transaction Failure'] || [];
      technicalAssistanceData = productAggregatedData['Technical Assistance'] || [];
      updatePersonalInformationData = productAggregatedData['Update Personal Information'] || [];
      accountClosureOrUpgradeData = productAggregatedData['Account Closure or Upgrade'] || [];
      eligibilityAndRequirementsData = productAggregatedData['Eligibility and Requirements'] || [];
      productFeaturesInquiryData = productAggregatedData['Product Features Inquiry'] || [];
      accountOpeningAssistanceData = productAggregatedData['Account Opening Assistance'] || [];
    } else if (selectedRadio === 'average_wait_time' || selectedRadio === 'average_handle_time' || selectedRadio === 'resolution_rate' || selectedRadio === 'average_sentiment') {
      // Choose the metric based on the selectedRadio
      let selectedMetric;
      if (selectedRadio === 'average_wait_time') {
        selectedMetric = 'average_waittime';
      } else if (selectedRadio === 'average_handle_time') {
        selectedMetric = 'average_handletime';
      } else if (selectedRadio === 'resolution_rate') {
        selectedMetric = 'resolution_rate';
      } else if (selectedRadio === 'average_sentiment') {
        selectedMetric = 'average_sentiment';
      }
      
      result.forEach(entry => {
        const { callsubreason, date, [selectedMetric]: metricValue } = entry;
    
        // Check if the callsubreason exists in productAggregatedData
        if (!productAggregatedData[callsubreason]) {
          productAggregatedData[callsubreason] = [];
        }
    
        // Check if there's an entry for the given date
        const existingEntry = productAggregatedData[callsubreason].find(item => item.date === date);
    
        // If there's an existing entry, update the metricValue
        if (existingEntry) {
          existingEntry[selectedMetric] += metricValue;
          existingEntry.call_count += 1;
        } else {
          // If no existing entry, create a new entry
          productAggregatedData[callsubreason].push({ date, [selectedMetric]: metricValue, call_count: 1 });
        }
      });
    
      // Calculate the average metricValue for each date
      for (const productName in productAggregatedData) {
        productAggregatedData[productName].forEach(entry => {
          entry[selectedMetric] /= entry.call_count;
          delete entry.call_count;
        });
      }

      transactionLimitsInquiryData = productAggregatedData['Transaction Limits Inquiry'] || [];
      unauthorizedTransactionData = productAggregatedData['Unauthorized Transaction'] || [];
      transactionFailureData = productAggregatedData['Transaction Failure'] || [];
      technicalAssistanceData = productAggregatedData['Technical Assistance'] || [];
      updatePersonalInformationData = productAggregatedData['Update Personal Information'] || [];
      accountClosureOrUpgradeData = productAggregatedData['Account Closure or Upgrade'] || [];
      eligibilityAndRequirementsData = productAggregatedData['Eligibility and Requirements'] || [];
      productFeaturesInquiryData = productAggregatedData['Product Features Inquiry'] || [];
      accountOpeningAssistanceData = productAggregatedData['Account Opening Assistance'] || [];
    }
    //console.log(productAggregatedData)
  } else if (selection === 'By Call Reason') {
    // Implement call reason chart here using the same logic as products
    if (selectedRadio === 'total_call_volume' || selectedRadio === 'unresolved_calls') {
      result.forEach(entry => {
        const reasonName = entry.callreason;

        if (!productAggregatedData[reasonName]) {
          productAggregatedData[reasonName] = [];
        }

        let entryDate;
        if (range === 'W') {
          const date = new Date(entry.date);
          date.setDate(date.getDate() - date.getDay());
          entryDate = date.toISOString().split('T')[0];
        } else {
          entryDate = entry.date;
        }

        const existingEntry = productAggregatedData[reasonName].find(item => item.date === entryDate);
        if (existingEntry) {
          existingEntry[dataKey] += entry[dataKey];
        } else {
          productAggregatedData[reasonName].push({ date: entryDate, [dataKey]: entry[dataKey] });
        }
      });

      accountMgmtData = productAggregatedData['Account Management'] || [];
      onlineMobileBankingData = productAggregatedData['Online and Mobile Banking'] || [];
      transactionIssuesData = productAggregatedData['Transaction Related'] || [];
      accountSetupData = productAggregatedData['Account Setup or Information'] || [];
    } else if (selectedRadio === 'average_wait_time' || selectedRadio === 'average_handle_time' || selectedRadio === 'resolution_rate' || selectedRadio === 'average_sentiment') {
      // Choose the metric based on the selectedRadio
      let selectedMetric;
      if (selectedRadio === 'average_wait_time') {
        selectedMetric = 'average_waittime';
      } else if (selectedRadio === 'average_handle_time') {
        selectedMetric = 'average_handletime';
      } else if (selectedRadio === 'resolution_rate') {
        selectedMetric = 'resolution_rate';
      } else if (selectedRadio === 'average_sentiment') {
        selectedMetric = 'average_sentiment';
      }
      
      result.forEach(entry => {
        const { callreason, date, [selectedMetric]: metricValue } = entry;
    
        // Check if the callreason exists in productAggregatedData
        if (!productAggregatedData[callreason]) {
          productAggregatedData[callreason] = [];
        }
    
        // Check if there's an entry for the given date
        const existingEntry = productAggregatedData[callreason].find(item => item.date === date);
    
        // If there's an existing entry, update the metricValue
        if (existingEntry) {
          existingEntry[selectedMetric] += metricValue;
          existingEntry.call_count += 1;
        } else {
          // If no existing entry, create a new entry
          productAggregatedData[callreason].push({ date, [selectedMetric]: metricValue, call_count: 1 });
        }
      });
    
      // Calculate the average metricValue for each date
      for (const reasonName in productAggregatedData) {
        productAggregatedData[reasonName].forEach(entry => {
          entry[selectedMetric] /= entry.call_count;
          delete entry.call_count;
        });
      }

      accountMgmtData = productAggregatedData['Account Management'] || [];
      onlineMobileBankingData = productAggregatedData['Online and Mobile Banking'] || [];
      transactionIssuesData = productAggregatedData['Transaction Related'] || [];
      accountSetupData = productAggregatedData['Account Setup or Information'] || [];
    }
  }

  // PRODUCT LABELS
  const processChartData = (data, selectedRadio, range, result, dataKey) => {
    if (selection === 'All') {
      data.forEach((entry) => {
        if (selectedRadio === 'average_wait_time' || selectedRadio === 'average_handle_time' || selectedRadio === 'resolution_rate' || selectedRadio === 'average_sentiment') {
          if (range === 'W') {
            entry[dataKey] /= 7;
          } else {
            entry[dataKey] /= result.filter((item) => item.date === entry.date).length;
          }
        }
      });
    }
  };
  
  let labels;
  if (selection === 'All') {
    labels = aggregatedData.map((entry) => (range === 'W' ? `${entry.date}` : entry.date));
    processChartData(aggregatedData, selectedRadio, range, result, dataKey);
  } else if (selection === 'By Call Sub Reason') {
    labels = transactionLimitsInquiryData.map((entry) => (range === 'W' ? `${entry.date}` : entry.date));
    processChartData(transactionLimitsInquiryData, selectedRadio, range, result, dataKey);
    processChartData(unauthorizedTransactionData, selectedRadio, range, result, dataKey);
    processChartData(transactionFailureData, selectedRadio, range, result, dataKey);
    processChartData(technicalAssistanceData, selectedRadio, range, result, dataKey);
    processChartData(updatePersonalInformationData, selectedRadio, range, result, dataKey);
    processChartData(accountClosureOrUpgradeData, selectedRadio, range, result, dataKey);
    processChartData(eligibilityAndRequirementsData, selectedRadio, range, result, dataKey);
    processChartData(productFeaturesInquiryData, selectedRadio, range, result, dataKey);
    processChartData(accountOpeningAssistanceData, selectedRadio, range, result, dataKey);
  } else if (selection === 'By Call Reason') {
    labels = accountMgmtData.map((entry) => (range === 'W' ? `${entry.date}` : entry.date));
    processChartData(accountMgmtData, selectedRadio, range, result, dataKey);
    processChartData(onlineMobileBankingData, selectedRadio, range, result, dataKey);
    processChartData(transactionIssuesData, selectedRadio, range, result, dataKey);
    processChartData(accountSetupData, selectedRadio, range, result, dataKey);
  }
  //console.log(labels);

  // PRODUCT STYLES
  const productMapping = {
    'Transaction Limits Inquiry': {
      label: 'Transaction Limits Inquiry',
      data: transactionLimitsInquiryData,
      borderColor: 'rgba(75, 192, 192, 1)',
      backgroundColor: 'rgba(75, 192, 192, 1)',
    },
    'Unauthorized Transaction': {
      label: 'Unauthorized Transaction',
      data: unauthorizedTransactionData,
      borderColor: 'rgba(255, 99, 132, 1)',
      backgroundColor: 'rgba(255, 99, 132, 1)',
    },
    'Transaction Failure': {
      label: 'Transaction Failure',
      data: transactionFailureData,
      borderColor: 'rgba(255, 205, 86, 1)',
      backgroundColor: 'rgba(255, 205, 86, 1)',
    },
    'Technical Assistance': {
      label: 'Technical Assistance',
      data: technicalAssistanceData,
      borderColor: 'rgba(128, 0, 128, 1)',
      backgroundColor: 'rgba(128, 0, 128, 1)',
    },
    'Update Personal Information': {
      label: 'Update Personal Information',
      data: updatePersonalInformationData,
      borderColor: 'rgba(0, 255, 255, 1)',
      backgroundColor: 'rgba(0, 255, 255, 1)',
    },
    'Account Closure or Upgrade': {
      label: 'Account Closure or Upgrade',
      data: accountClosureOrUpgradeData,
      borderColor: 'rgba(255, 165, 0, 1)',
      backgroundColor: 'rgba(255, 165, 0, 1)',
    },
    'Eligibility and Requirements': {
      label: 'Eligibility and Requirements',
      data: eligibilityAndRequirementsData,
      borderColor: 'rgba(255, 192, 203, 1)',
      backgroundColor: 'rgba(255, 192, 203, 1)',
    },
    'Product Features Inquiry': {
      label: 'Product Features Inquiry',
      data: productFeaturesInquiryData,
      borderColor: 'rgba(128, 128, 0, 1)',
      backgroundColor: 'rgba(128, 128, 0, 1)',
    },
    'Account Opening Assistance': {
      label: 'Account Opening Assistance',
      data: accountOpeningAssistanceData,
      borderColor: 'rgba(0, 255, 0, 1)',
      backgroundColor: 'rgba(0, 255, 0, 1)',
    },
  };  

  // CALL REASON STYLES
  const callReasonMapping = {
    'Account Management': {
      label: 'Account Management',
      data: accountMgmtData,
      borderColor: 'rgba(255, 99, 132, 1)',
      backgroundColor: 'rgba(255, 99, 132, 1)',
    },
    'Online and Mobile Banking': {
      label: 'Online and Mobile Banking',
      data: onlineMobileBankingData,
      borderColor: 'rgba(255, 205, 86, 1)',
      backgroundColor: 'rgba(255, 205, 86, 1)',
    },
    'Transaction Related': {
      label: 'Transaction Related',
      data: transactionIssuesData,
      borderColor: 'rgba(54, 162, 235, 1)',
      backgroundColor: 'rgba(54, 162, 235, 1)',
    },
    'Account Setup or Information': {
      label: 'Account Setup or Information',
      data: accountSetupData,
      borderColor: 'rgba(153, 102, 255, 1)',
      backgroundColor: 'rgba(153, 102, 255, 1)',
    },
  }
  
  // PRODUCT DATA
  let data;
  if (selection === 'All' && mode === 'light') {
    data = {
      labels,
      datasets: [
        {
          label: dataLabel ? dataLabel : 'Call Volume',
          data: aggregatedData.map((entry) => Math.round(entry[dataKey])),
          borderColor: theme.colors.secondary,
          backgroundColor: theme.colors.primary,
        },
      ],
    };
  } else if (selection === 'All' && mode === 'dark') {
    data = {
      labels,
      datasets: [
        {
          label: dataLabel ? dataLabel : 'Call Volume',
          data: aggregatedData.map((entry) => Math.round(entry[dataKey])),
          borderColor: theme.colors.primary,
          backgroundColor: theme.colors.primary,
        },
      ],
    };
  }  else if (selection === 'By Call Sub Reason') {
    if (selectedSubReasonFromChild === '') {
      // Show all product data
      const allProductsData = Object.values(productMapping).map((product) => ({
        label: product.label,
        data: product.data.map((entry) => Math.round(entry[dataKey])),
        borderColor: product.borderColor,
        backgroundColor: product.backgroundColor,
      }));
  
      data = {
        labels,
        datasets: allProductsData,
      };
    } else {
      // Show individual product data
      const selectedProduct = productMapping[selectedSubReasonFromChild];
  
      data = {
        labels,
        datasets: [
          {
            label: selectedProduct.label,
            data: selectedProduct.data.map((entry) => Math.round(entry[dataKey])),
            borderColor: selectedProduct.borderColor,
            backgroundColor: selectedProduct.backgroundColor,
          },
        ],
      };
    }
  } else if (selection === 'By Call Reason') {
    if (selectedReasonFromChild === '') {
      // Show all product data
      const allProductsData = Object.values(callReasonMapping).map((product) => ({
        label: product.label,
        data: product.data.map((entry) => Math.round(entry[dataKey])),
        borderColor: product.borderColor,
        backgroundColor: product.backgroundColor,
      }));
  
      data = {
        labels,
        datasets: allProductsData,
      };
    } else {
      // Show individual product data
      const selectedProduct = callReasonMapping[selectedReasonFromChild];

      data = {
        labels,
        datasets: [
          {
            label: selectedProduct.label,
            data: selectedProduct.data.map((entry) => Math.round(entry[dataKey])),
            borderColor: selectedProduct.borderColor,
            backgroundColor: selectedProduct.backgroundColor,
          },
        ],
      };
    }
  };

  const options = {
    maintainAspectRatio: false,
    responsive: true,
    plugins: {
      datalabels: {
        display: true,
        color: mode === 'light' ? 'black': 'white',
        align: 'end',
        padding: {
          top: 0,
          left: 5,
        },
        zIndex: 5,
        labels: {
          padding: { top: 10 },
          title: {
            font: {
              weight: 'light',
            },
          },
          value: {
            color: mode === 'light' ? 'black': 'white',
          },
        },
        formatter: function (value, context) {
          return context.chart.data.datasets[0].data[context.chart.data.labels[context.dataIndex]];
        },
      },
      legend: {
        display: true,
        position: 'top',
        labels: {
          boxWidth: 10,
          color: mode === 'light' ? '#797979': '#b3b3b3',
        },
      },
      title: {
        display: false,
      },
    },
    scales: {
      x: {
        grace: '10%',
        ticks: {
          color: mode === 'light' ? '#797979': '#b3b3b3',
        },
        grid: {
          color: mode === 'light' ? '#e4e4e4': '#5e5e5e',
        }
      },
      y: {
        title: {
          display: true,
          text: dataLabel ? dataLabel : 'Call Volume',
          color: mode === 'light' ? '#797979': '#b3b3b3',
        },
        grace: '5%',
        ticks: {
          color: mode === 'light' ? '#797979': '#b3b3b3',
        },
        grid: {
          color: mode === 'light' ? '#e4e4e4': '#5e5e5e',
        }
      },
    },
  };

  return (
    <div className='exec-chart-section'>
      <div className='exec-chart-header'>
        <h3>{displayText}{(displayText === 'Resolution Rate' || displayText === 'Average Sentiment' || displayText === 'Unresolved Calls') && (mode === 'dark' ? <span className='shine'><AutoAwesomeIcon/></span> : <span className='ai-driven'><AutoAwesomeIcon/></span>)}</h3>
        <FormControl className='chart-form' sx={{ width: '150px', marginLeft: '15px' }}>
          <InputLabel variant="standard" htmlFor="uncontrolled-native">
            Filter
          </InputLabel>
          <NativeSelect
            value={selection}
            onChange={handleSelection}
            inputProps={{
              name: 'filter',
              id: 'uncontrolled-native',
            }}
          >
            <option value={'All'}>All</option>
            <option value={'By Call Reason'}>By Call Reason</option>
            <option value={'By Call Sub Reason'}>By Call Sub Reason</option>
          </NativeSelect>
        </FormControl>
        {selectedOption !== 'Report Date' && selectedOption !== 'Last 7 days' && (
          <FormControl className='chart-form' sx={{ width: '100px' }}>
            <InputLabel variant="standard" htmlFor="uncontrolled-native">
              Interval
            </InputLabel>
            <NativeSelect
              defaultValue={'D'}
              onChange={handleChange}
              inputProps={{
                name: 'range',
                id: 'uncontrolled-native',
              }}
            >
              <option value={'D'}>Daily</option>
              <option value={'W'}>Weekly</option>
            </NativeSelect>
          </FormControl>
        )}
      </div>
      <div className='exec-chart-canvas'>
        {isLoading ? (
          <Skeleton height={250} width={'100%'} />
        ) : (
          <div className="chart-container">
            <Line options={options} data={data} plugins={[ChartDataLabels]} />
          </div>
        )}
      </div>
    </div>
  );
}

export default OpsChart;